/**
 * 柜档案 / IoT 绑定 API（对齐 ContainerController）。
 *
 * 真实端点（经网关 /api，StripPrefix=1）：
 * - POST /container/{id}/iot/bind    装柜节点 seal_iot 子步骤：封柜绑定 IoT（履约封柜 IoT 子步骤提交入口）
 * - GET  /container/{id}             柜档案详情
 */
import { request } from './request';

export interface ContainerBindPayload {
  deviceNo: string;
  boundType: string;
  remark?: string;
  operator?: string;
}

export function bindIotDevice(containerId: string, payload: ContainerBindPayload): Promise<unknown> {
  return request(`/container/${containerId}/iot/bind`, { method: 'POST', body: payload });
}

export function getContainer(containerId: string): Promise<unknown> {
  return request(`/container/${containerId}`);
}

/** 柜档案（移动端仅消费，未知字段用索引签名兜底） */
export interface ContainerInfo {
  id: string;
  containerNo?: string;
  sealNo?: string;
  status?: string;
  orderId?: string;
  [key: string]: unknown;
}

/** 按订单 ID 查柜档案（一单多柜；load 节点作业前必须先选柜） */
export function getContainersByOrder(orderId: string): Promise<ContainerInfo[]> {
  return request<ContainerInfo[]>(`/container/listByOrderId/${orderId}`);
}

/** 封柜二次录入封号交叉比对（T8 强校验，不一致返回 400 不软跳过） */
export function sealConfirm(containerId: string, sealNo: string): Promise<boolean> {
  return request<boolean>('/container/seal-confirm', {
    method: 'POST',
    body: { containerId, sealNo },
  });
}

/** 节点配置条目（对齐后端 Node 表 / GET /container/nodes） */
export interface NodeItem {
  nodeCode: string;
  nodeName: string;
  nodeSeq?: number;
  [key: string]: unknown;
}

/** 全量节点配置（节点名唯一事实来源；登录后拉取，名称实时与后端 nodes 表对齐） */
export function getNodes(): Promise<NodeItem[]> {
  return request<NodeItem[]>('/container/nodes');
}

// ==================== 节点记录 API（方案A：已完成订单详情展示） ====================

/** 节点记录（对齐 NodeRecord 实体） */
export interface NodeRecordItem {
  id: string;
  orgId?: string;
  containerId: string; // 实际存储的是 orderId
  nodeCode: string;
  nodeSeq: number;
  status: string;
  operatorId?: string;
  operatorName?: string;
  occurredAt?: string; // 节点实际完成时间
  photos?: string; // JSONB 数组
  notes?: string;
  evidenceIds?: string;
  createdAt?: string;
  createdBy?: string;
}

/**
 * 按订单 ID 获取节点记录列表
 * 注意：node_records 表的 container_id 字段实际存储的是 orderId
 */
export function getNodeRecordsByOrder(orderId: string): Promise<NodeRecordItem[]> {
  return request<NodeRecordItem[]>('/container/node/list', {
    params: { containerId: orderId },
  });
}

/** 获取节点单据/附件列表 */
export interface NodeDocumentItem {
  id: string;
  nodeRecordId?: string;
  docType?: string;
  fileId?: string;
  fileName?: string;
  fileUrl?: string;
  createdAt?: string;
  [key: string]: unknown;
}

export function getNodeDocuments(nodeRecordId: string): Promise<NodeDocumentItem[]> {
  return request<NodeDocumentItem[]>('/container/node/documents', {
    params: { nodeRecordId },
  });
}

/** 获取文件下载 URL */
export function getFileUrl(
  fileId: string,
  host: string = typeof window !== 'undefined' ? window.location.hostname : '',
): Promise<string> {
  const params: Record<string, string> = {};
  if (host) params.host = host;
  return request<string>(`/file/${fileId}/url`, { params });
}
